import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Alert,
    CircularProgress,
    Paper,
    Avatar,
    InputBase,
    FormControl,
    InputLabel,
    Select,
    MenuItem as SelectMenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    People,
    Add,
    Edit,
    Delete,
    MoreVert,
    Visibility,
    Search,
    Business,
    Email,
    Public
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    deleteDoc
} from 'firebase/firestore';
import { db } from '../../firebaseClient';

export interface Consultant {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    company: string;
    jobTitle?: string;
    country: string;
    isRegisteredUser: boolean;
    isActive: boolean;
    tenantId?: string;
    createdBy?: string;
    createdAt: any;
}

const ConsultantList: React.FC = () => {
    const { user, userProfile, isTenantAdmin, isGlobalAdmin } = useUser();
    useDocumentTitle('Consultants');
    const navigate = useNavigate();

    const [consultants, setConsultants] = useState<Consultant[]>([]);
    const [filteredConsultants, setFilteredConsultants] = useState<Consultant[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedConsultant, setSelectedConsultant] = useState<Consultant | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [countryFilter, setCountryFilter] = useState('');

    // Extracted unique values for filters
    const [companies, setCompanies] = useState<string[]>([]);
    const [countries, setCountries] = useState<string[]>([]);

    useEffect(() => {
        if (user && userProfile && (isTenantAdmin() || isGlobalAdmin())) {
            loadConsultants();
        }
    }, [user, userProfile]);

    useEffect(() => {
        applyFilters();
    }, [consultants, searchQuery, companyFilter, countryFilter]);

    const loadConsultants = () => {
        try {
            setLoading(true);
            setError('');

            let consultantsQuery;

            if (isGlobalAdmin()) {
                // Global admins can see all consultants
                consultantsQuery = query(
                    collection(db, 'consultants')
                );
            } else if (userProfile?.tenantId) {
                // Tenant admins see only their tenant's consultants
                consultantsQuery = query(
                    collection(db, 'consultants'),
                    where('tenantId', '==', userProfile.tenantId)
                );
            } else {
                setError('User tenant information not found');
                setLoading(false);
                return;
            }

            const unsubscribe = onSnapshot(consultantsQuery, (snapshot) => {
                const consultantList: Consultant[] = [];
                const companySet = new Set<string>();
                const countrySet = new Set<string>();

                snapshot.forEach((doc) => {
                    const consultantData = { id: doc.id, ...doc.data() } as Consultant;
                    consultantList.push(consultantData);

                    if (consultantData.company) companySet.add(consultantData.company);
                    if (consultantData.country) countrySet.add(consultantData.country);
                });

                // Sort consultants by lastName client-side to avoid needing composite index
                consultantList.sort((a, b) => {
                    const lastNameA = (a.lastName || '').toLowerCase();
                    const lastNameB = (b.lastName || '').toLowerCase();
                    return lastNameA.localeCompare(lastNameB);
                });

                setConsultants(consultantList);
                setCompanies(Array.from(companySet).sort());
                setCountries(Array.from(countrySet).sort());
                setLoading(false);
            }, (error) => {
                console.error('Error loading consultants:', error);
                setError('Failed to load consultants');
                setLoading(false);
            });

            return () => unsubscribe();
        } catch (error: any) {
            console.error('Error setting up consultant listener:', error);
            setError('Failed to load consultants');
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...consultants];

        // Apply search filter
        if (searchQuery) {
            const queryText = searchQuery.toLowerCase();
            filtered = filtered.filter(consultant =>
                consultant.firstName?.toLowerCase().includes(queryText) ||
                consultant.lastName?.toLowerCase().includes(queryText) ||
                consultant.email?.toLowerCase().includes(queryText) ||
                consultant.company?.toLowerCase().includes(queryText)
            );
        }

        // Apply company filter
        if (companyFilter) {
            filtered = filtered.filter(consultant => consultant.company === companyFilter);
        }

        // Apply country filter
        if (countryFilter) {
            filtered = filtered.filter(consultant => consultant.country === countryFilter);
        }

        setFilteredConsultants(filtered);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, consultant: Consultant) => {
        setMenuAnchor(event.currentTarget);
        setSelectedConsultant(consultant);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedConsultant(null);
    };

    const handleViewConsultant = () => {
        if (selectedConsultant) {
            navigate(`/consultants/${selectedConsultant.id}`);
        }
        handleMenuClose();
    };

    const handleEditConsultant = () => {
        if (selectedConsultant) {
            navigate(`/consultants/${selectedConsultant.id}/edit`);
        }
        handleMenuClose();
    };

    const handleDeleteConsultant = async () => {
        if (!selectedConsultant) return;

        try {
            await deleteDoc(doc(db, 'consultants', selectedConsultant.id));
            setDeleteDialogOpen(false);
            handleMenuClose();
        } catch (error) {
            console.error('Error deleting consultant:', error);
            setError('Failed to delete consultant');
        }
    };

    const canEditConsultant = (consultant: Consultant | null) => {
        if (!consultant || !userProfile || !user) return false;
        return isGlobalAdmin() || (isTenantAdmin() && consultant.tenantId === userProfile.tenantId);
    };

    const getInitials = (firstName: string, lastName: string) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        if (timestamp.seconds) {
            return new Date(timestamp.seconds * 1000).toLocaleDateString();
        }
        return new Date(timestamp).toLocaleDateString();
    };

    if (!isTenantAdmin() && !isGlobalAdmin()) {
        return (
            <Alert severity="error">
                You don't have permission to manage consultants.
            </Alert>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Consultants
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage consultant profiles and information
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/consultants/new')}
                >
                    Add Consultant
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1, px: 1 }}>
                            <Search sx={{ color: 'text.secondary', mr: 1 }} />
                            <InputBase
                                placeholder="Search consultants..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{ width: '100%', py: 1 }}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Company</InputLabel>
                            <Select
                                value={companyFilter}
                                onChange={(e) => setCompanyFilter(e.target.value)}
                                label="Company"
                            >
                                <SelectMenuItem value="">All Companies</SelectMenuItem>
                                {companies.map((company) => (
                                    <SelectMenuItem key={company} value={company}>
                                        {company}
                                    </SelectMenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Country</InputLabel>
                            <Select
                                value={countryFilter}
                                onChange={(e) => setCountryFilter(e.target.value)}
                                label="Country"
                            >
                                <SelectMenuItem value="">All Countries</SelectMenuItem>
                                {countries.map((country) => (
                                    <SelectMenuItem key={country} value={country}>
                                        {country}
                                    </SelectMenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Consultant List */}
            {filteredConsultants.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'grey.100', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <People />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {consultants.length === 0 ? 'No consultants found' : 'No consultants match your filters'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {consultants.length === 0
                            ? 'Add your first consultant to get started'
                            : 'Try adjusting your search or filter criteria'
                        }
                    </Typography>
                    {consultants.length === 0 && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => navigate('/consultants/new')}
                        >
                            Add Consultant
                        </Button>
                    )}
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {filteredConsultants.map((consultant) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={consultant.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                                {getInitials(consultant.firstName, consultant.lastName)}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 600,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {consultant.firstName} {consultant.lastName}
                                                </Typography>
                                                {consultant.jobTitle && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {consultant.jobTitle}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, consultant)}
                                        >
                                            <MoreVert />
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        {consultant.company && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Business sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {consultant.company}
                                                </Typography>
                                            </Box>
                                        )}
                                        {consultant.email && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Email sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {consultant.email}
                                                </Typography>
                                            </Box>
                                        )}
                                        {consultant.country && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Public sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {consultant.country}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        {consultant.isRegisteredUser ? (
                                            <Chip label="Registered User" size="small" color="success" variant="outlined" />
                                        ) : (
                                            <Chip label="Third Party" size="small" color="default" variant="outlined" />
                                        )}
                                        {consultant.isActive && (
                                            <Chip label="Active" size="small" color="primary" variant="outlined" />
                                        )}
                                    </Box>

                                    <Typography variant="caption" color="text.secondary">
                                        Added: {formatDate(consultant.createdAt)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Action Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleViewConsultant}>
                    <ListItemIcon>
                        <Visibility fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View Details</ListItemText>
                </MenuItem>

                {canEditConsultant(selectedConsultant) && (
                    <MenuItem onClick={handleEditConsultant}>
                        <ListItemIcon>
                            <Edit fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>
                )}

                {canEditConsultant(selectedConsultant) && (
                    <MenuItem
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={{ color: 'error.main' }}
                    >
                        <ListItemIcon>
                            <Delete fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Consultant</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{selectedConsultant?.firstName} {selectedConsultant?.lastName}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConsultant} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ConsultantList;
